import { fillPath, httpMethodSchema, route } from "./routes.js";
import { isRequestPath } from "./request-path.js";
import { z } from "zod";
import { STEP_UP_PATH_MAX_LENGTH, webAuthnAuthenticationResponseSchema } from "@zion/sdk-contracts";
//#region src/step-up-routes.ts
/**
* `/v1/auth/step-up/requests`: the four DP step-up request routes (kernel apps, WS-2b,
* `design-docs/WS-2.md` section 3.5, R-003).
*
* A step-up request holds one privileged operation until a human approves it with a passkey on the
* kernel origin. The record, the server summary and the one view that the step-up page reads live
* in `@zion/sdk-contracts` (`kernel/step-up.ts`, WS-1b): `stepUpRequestViewSchema`,
* `StepUpRequestView`, `stepUpPublicKeyOptionsSchema` (R-003). This module declares the request
* bodies, the decision answer, the routes and the refusal codes, and no second view.
*
* Producer: core-api's step-up module (WS-12a) in `cloud/dp/core-api/src/kernel/step-up/`.
* Consumers: the step-up page (WS-15), `useStepUp()` (WS-17) and the org admin app (EX-1).
*
* Kernel class: `kernel-session` on each route (`CONTRACT_ROUTE_KERNEL_CLASS` in
* `route-census.ts`). A caller without a kernel session token gets the 403
* `kernel_origin_required` body of `@zion/sdk-contracts`. The four routes sit in the kernel fold
* `KERNEL_CONTRACT_ROUTES`.
*
* The refusals of an `{id}` route come in this order:
*
* 1. 404 `step_up_request_not_found`: an unknown id, or the id of another org.
* 2. 410 `step_up_request_expired`: the request waited longer than 24 hours (I-11).
* 3. 409 `step_up_request_not_pending`: the request is already approved, denied or consumed.
* 4. 403 `step_up_approver_lacks_permission`: the approver lacks the permission of the operation.
* 5. 403 `step_up_assertion_invalid`: the assertion failed. It is 403 and never 401, because the
*    SDK turns a 401 into a refresh, a retry and then a logout (r5 claim 20).
*
* Direction rule (R-28): each request schema is `z.strictObject`; the decision answer is
* `z.object`. No schema here carries a `.transform()`, so `createOpenApiDocument()` can represent
* each one.
*/
/** The collection path. It has no parameter. */
const STEP_UP_REQUESTS_PATH = "/v1/auth/step-up/requests";
/** One request, in OpenAPI `{param}` form. Fill it with {@link stepUpRequestPath}. */
const STEP_UP_REQUEST_PATH_TEMPLATE = `${STEP_UP_REQUESTS_PATH}/{id}`;
/** The approval of one request. Fill it with {@link stepUpAssertionPath}. */
const STEP_UP_ASSERTION_PATH_TEMPLATE = `${STEP_UP_REQUEST_PATH_TEMPLATE}/assertion`;
/** The refusal of one request. Fill it with {@link stepUpDenyPath}. */
const STEP_UP_DENY_PATH_TEMPLATE = `${STEP_UP_REQUEST_PATH_TEMPLATE}/deny`;
/**
* `POST /v1/auth/step-up/requests`. Body {@link createStepUpRequestBodySchema}. core-api resolves
* `path` with `matchContractRoute` and `kernelClassOf`. No match, or a class other than `kernel`
* at tier 1 or 2, answers 400 `step_up_not_applicable`. Success answers 201 with the
* `stepUpRequestViewSchema` view of `@zion/sdk-contracts`, status `pending`, with WebAuthn options.
*/
const CREATE_STEP_UP_REQUEST_ROUTE = route("POST", STEP_UP_REQUESTS_PATH);
/**
* `GET /v1/auth/step-up/requests/{id}`. Status 200 with the `stepUpRequestViewSchema` view of
* `@zion/sdk-contracts`. The summary lines are server text, built from the stored route and body,
* never from caller text. The view carries WebAuthn options with `userVerification: 'required'`
* while the request is `pending`, and `null` after it. Refusals 404 and 410 of the module order.
*/
const GET_STEP_UP_REQUEST_ROUTE = route("GET", STEP_UP_REQUEST_PATH_TEMPLATE);
/**
* `POST /v1/auth/step-up/requests/{id}/assertion`. Body {@link submitStepUpAssertionBodySchema}.
* Status 200 with {@link stepUpDecisionResponseSchema}, status `approved`. `usableUntil` is at most
* 600 seconds after the approval (`STEP_UP_APPROVAL_TTL_SECONDS`, I-11). Refusals 1 to 5 of the
* module order.
*/
const SUBMIT_STEP_UP_ASSERTION_ROUTE = route("POST", STEP_UP_ASSERTION_PATH_TEMPLATE);
/**
* `POST /v1/auth/step-up/requests/{id}/deny`. Body {@link denyStepUpRequestBodySchema}, the empty
* object. Status 200 with {@link stepUpDecisionResponseSchema}, status `denied` and
* `usableUntil: null`. A deny needs no passkey, because it removes no authority. Refusals 1 to 4 of
* the module order.
*/
const DENY_STEP_UP_REQUEST_ROUTE = route("POST", STEP_UP_DENY_PATH_TEMPLATE);
/**
* The path of the operation that a step-up request approves: 1 to 512 characters
* (`STEP_UP_PATH_MAX_LENGTH` of `@zion/sdk-contracts`, the bound of the stored record), then the
* request-path grammar of `request-path.ts`. A length failure aborts the parse, so the grammar never
* runs on an over-length text. So the path starts with `/v1/`, holds no `?`, `#`,
* empty segment or bare `%`, and no dot-segment in any spelling (`design-docs/WS-2.md` open
* question 10: a step-up never carries a query).
*/
const stepUpOperationPathField = z.string().min(1, { abort: true }).max(STEP_UP_PATH_MAX_LENGTH, { abort: true }).refine(isRequestPath, { message: "a request path under /v1/ with no query, fragment or dot-segment" });
/**
* The body of {@link CREATE_STEP_UP_REQUEST_ROUTE}, strict:
*
* - `method`: `POST`, `PUT`, `PATCH` or `DELETE`. `GET` fails, because no read owes a step-up.
* - `path`: the operation path, see `stepUpOperationPathField`.
* - `body`: the operation body, any JSON value, or absent for an operation with no body.
*
* Any other key fails.
*/
const createStepUpRequestBodySchema = z.strictObject({
	method: httpMethodSchema.exclude(["GET"]),
	path: stepUpOperationPathField,
	body: z.unknown().optional()
});
/**
* The body of {@link SUBMIT_STEP_UP_ASSERTION_ROUTE}, strict: `{ response }`, where `response` is the
* `AuthenticationResponseJSON` that `startAuthentication()` returns, under the strict
* `webAuthnAuthenticationResponseSchema` of `@zion/sdk-contracts` (index defaults rows 1 and 2).
* An array, a missing member or any other key fails.
*/
const submitStepUpAssertionBodySchema = z.strictObject({ response: webAuthnAuthenticationResponseSchema });
/** The body of {@link DENY_STEP_UP_REQUEST_ROUTE}: the empty object, strict. A `reason` or any other key fails. */
const denyStepUpRequestBodySchema = z.strictObject({});
/**
* The 200 body of the assertion and deny routes, one of two arms on `status`:
*
* - `approved`: `usableUntil` is the ISO 8601 UTC end of the one use;
* - `denied`: `usableUntil` is `null`.
*
* `id` is the request uuid. Any other status, `pending` among them, fails, and so does an arm with
* the other arm's `usableUntil`.
*/
const stepUpDecisionResponseSchema = z.discriminatedUnion("status", [z.object({
	id: z.uuid(),
	status: z.literal("approved"),
	usableUntil: z.iso.datetime()
}), z.object({
	id: z.uuid(),
	status: z.literal("denied"),
	usableUntil: z.null()
})]);
/**
* The six refusal codes of the step-up routes: `step_up_not_applicable` of the create route, then
* the five of the `{id}` routes in the order of the module document.
*/
const STEP_UP_ROUTE_ERROR_CODES = Object.freeze([
	"step_up_not_applicable",
	"step_up_request_not_found",
	"step_up_request_not_pending",
	"step_up_request_expired",
	"step_up_assertion_invalid",
	"step_up_approver_lacks_permission"
]);
/**
* Fill {@link STEP_UP_REQUEST_PATH_TEMPLATE} with one request id.
*
* @throws a `ZodError` when `requestId` is not a uuid. Pure.
*/
function stepUpRequestPath(requestId) {
	return fillPath(STEP_UP_REQUEST_PATH_TEMPLATE, { id: requestId }, { id: z.uuid() });
}
/**
* Fill {@link STEP_UP_ASSERTION_PATH_TEMPLATE} with one request id.
*
* @throws a `ZodError` when `requestId` is not a uuid. Pure.
*/
function stepUpAssertionPath(requestId) {
	return fillPath(STEP_UP_ASSERTION_PATH_TEMPLATE, { id: requestId }, { id: z.uuid() });
}
/**
* Fill {@link STEP_UP_DENY_PATH_TEMPLATE} with one request id.
*
* @throws a `ZodError` when `requestId` is not a uuid. Pure.
*/
function stepUpDenyPath(requestId) {
	return fillPath(STEP_UP_DENY_PATH_TEMPLATE, { id: requestId }, { id: z.uuid() });
}
/** Every route this file declares, for the kernel fold of `route-census.ts`. */
const stepUpRoutes = Object.freeze([
	CREATE_STEP_UP_REQUEST_ROUTE,
	GET_STEP_UP_REQUEST_ROUTE,
	SUBMIT_STEP_UP_ASSERTION_ROUTE,
	DENY_STEP_UP_REQUEST_ROUTE
]);
//#endregion
export { CREATE_STEP_UP_REQUEST_ROUTE, DENY_STEP_UP_REQUEST_ROUTE, GET_STEP_UP_REQUEST_ROUTE, STEP_UP_ASSERTION_PATH_TEMPLATE, STEP_UP_DENY_PATH_TEMPLATE, STEP_UP_REQUESTS_PATH, STEP_UP_REQUEST_PATH_TEMPLATE, STEP_UP_ROUTE_ERROR_CODES, SUBMIT_STEP_UP_ASSERTION_ROUTE, createStepUpRequestBodySchema, denyStepUpRequestBodySchema, stepUpAssertionPath, stepUpDecisionResponseSchema, stepUpDenyPath, stepUpRequestPath, stepUpRoutes, submitStepUpAssertionBodySchema };

//# sourceMappingURL=step-up-routes.js.map